import type { useGT } from 'gt-react';
import {
  FORECAST_DEMAND_CLASSES,
  FORECAST_POPULARITY_CODE_MAX_LENGTH,
  FORECAST_SETTINGS_BOUNDS,
  type ForecastDemandClass,
  type ForecastSettingKey,
  type PlanningLevelStepKind,
  planningLevelRulesSchema,
} from '@erp/shared-types';
import type { DraftError } from './forecastSettingsDraft';

type Gt = ReturnType<typeof useGT>;

/** A setting's value as a history entry carries it; null = not set. */
export type ForecastSettingValue =
  number | Record<string, number> | { order: PlanningLevelStepKind[] } | null;

/** Every setting's display name, keyed by setting. */
export function settingLabels(gt: Gt): Record<ForecastSettingKey, string> {
  const context = 'forecast setting name';
  return {
    planningLevelRules: gt('Planning level rules', { context }),
    historyMonths: gt('History window', { context }),
    horizonMonths: gt('Forecast horizon', { context }),
    availabilityByClass: gt('Target availability by demand class', { context }),
    availabilityByPopularity: gt('Target availability by popularity code', {
      context,
    }),
    leadTimeDefaultDays: gt('Flat lead-time fallback', { context }),
    maxUnobservedLeadTimeShare: gt('Unobserved lead-time guard', { context }),
  };
}

export function settingLabel(setting: ForecastSettingKey, gt: Gt): string {
  return settingLabels(gt)[setting];
}

export function demandClassLabel(demandClass: ForecastDemandClass, gt: Gt) {
  switch (demandClass) {
    case 'smooth':
      return gt('Smooth', { context: 'forecast demand class' });
    case 'erratic':
      return gt('Erratic', { context: 'forecast demand class' });
    case 'intermittent':
      return gt('Intermittent', { context: 'forecast demand class' });
    case 'lumpy':
      return gt('Lumpy', { context: 'forecast demand class' });
  }
}

/** A 0..1 share as a percent, to at most two decimals (0.975 → "97.5%"). */
export function formatShare(share: number): string {
  return `${Number((share * 100).toFixed(2))}%`;
}

function isDemandClass(key: string): key is ForecastDemandClass {
  return (FORECAST_DEMAND_CLASSES as readonly string[]).includes(key);
}

function formatClassTargets(targets: Record<string, number>, gt: Gt) {
  const parts: string[] = [];
  for (const key of FORECAST_DEMAND_CLASSES) {
    if (key in targets) {
      parts.push(`${demandClassLabel(key, gt)} ${formatShare(targets[key])}`);
    }
  }
  for (const [key, share] of Object.entries(targets)) {
    if (!isDemandClass(key)) parts.push(`${key} ${formatShare(share)}`);
  }
  return parts.join(', ');
}

function formatPopularityTargets(targets: Record<string, number>, gt: Gt) {
  const codes = Object.keys(targets).sort();
  if (codes.length === 0) {
    return gt('None', { context: 'no popularity-code targets configured' });
  }
  return codes
    .map((code) => `${code} ${formatShare(targets[code])}`)
    .join(', ');
}

/** One setting's value for display; `null` reads as the engine default. */
export function formatSettingValue(
  setting: ForecastSettingKey,
  value: ForecastSettingValue,
  gt: Gt,
): string {
  if (value === null) {
    return gt('Default', { context: 'forecast setting not set, uses default' });
  }
  if (typeof value === 'number') {
    switch (setting) {
      case 'historyMonths':
      case 'horizonMonths':
        return gt('{n} months', {
          context: 'forecast setting value',
          n: value,
        });
      case 'leadTimeDefaultDays':
        return gt('{n} days', { context: 'forecast setting value', n: value });
      default:
        return formatShare(value);
    }
  }
  const rules = planningLevelRulesSchema.safeParse(value);
  if (rules.success) {
    return rules.data.order.length === 0
      ? gt('Calculated fallback', { context: 'empty planning rule order' })
      : rules.data.order.map((kind) => planningStepLabel(kind, gt)).join(' → ');
  }
  const targets = Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number',
    ),
  );
  return setting === 'availabilityByClass'
    ? formatClassTargets(targets, gt)
    : formatPopularityTargets(targets, gt);
}

export function draftErrorMessage(error: DraftError, gt: Gt): string {
  const context = 'forecast settings validation message';
  switch (error) {
    case 'historyMonthsRange':
      return gt('Enter a whole number of months from {min} to {max}.', {
        context,
        ...FORECAST_SETTINGS_BOUNDS.historyMonths,
      });
    case 'horizonMonthsRange':
      return gt('Enter a whole number of months from {min} to {max}.', {
        context,
        ...FORECAST_SETTINGS_BOUNDS.horizonMonths,
      });
    case 'leadTimeDaysRange':
      return gt('Enter a whole number of days from {min} to {max}.', {
        context,
        ...FORECAST_SETTINGS_BOUNDS.leadTimeDefaultDays,
      });
    case 'availabilityRange':
      return gt(
        'Enter a percent above 0 and below 100, with at most two decimals.',
        { context },
      );
    case 'guardRange':
      return gt(
        'Enter a percent above 0, up to 100, with at most two decimals.',
        {
          context,
        },
      );
    case 'codeRequired':
      return gt('Enter a popularity code.', { context });
    case 'codeTooLong':
      return gt('Use at most {max} characters.', {
        context,
        max: FORECAST_POPULARITY_CODE_MAX_LENGTH,
      });
    case 'codeDuplicate':
      return gt('This code is already listed.', { context });
    case 'targetRequired':
      return gt('Enter a target percent.', { context });
  }
}

export function planningStepLabel(kind: PlanningLevelStepKind, gt: Gt): string {
  switch (kind) {
    case 'fixed':
      return gt('Fixed min / max', { context: 'planning level rule step' });
    case 'higher_of_calculated_and_floor':
      return gt('Higher of calculated and floor', {
        context: 'planning level rule step',
      });
    case 'calculated':
      return gt('Calculated', { context: 'planning level rule step' });
  }
}
